import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildExternalResultsExplanationPrompt,
  EXTERNAL_RESULTS_EXPLANATION_PROMPT_FILENAME,
} from './services/aiResultsExplanation';
import { buildExternalProfileSummary } from './services/externalPlanWorkflow';
import { exportResultsExplanationToWord } from './services/exportToWord';
import {
  loadResultsExplanationDraft,
  saveResultsExplanationDraft,
} from './services/resultsExplanationDraft';

function draftSignature(value) {
  return `${value.text}\u0000${value.reviewConfirmed}`;
}

// Kept exported so the hydration race can be covered without a browser-only test harness.
// eslint-disable-next-line react-refresh/only-export-components
export function getResultsExplanationHydrationMode({ keepLocalText, latest, draft }) {
  if (!keepLocalText) return 'use-server';
  return draftSignature(latest) === draftSignature(draft) ? 'use-server' : 'save-local';
}

export default function ResultsExplanationTab({ client, consultation, hasPlan = false }) {
  const [text, setText] = useState('');
  const [privacyConfirmed, setPrivacyConfirmed] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [copyState, setCopyState] = useState('idle');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [draftState, setDraftState] = useState('loading');
  const revisionRef = useRef(0);
  const hydratedRef = useRef(false);
  const savedSignatureRef = useRef('');
  const saveTimerRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());
  const latestRef = useRef({ text: '', reviewConfirmed: false });
  const contextVersionRef = useRef(0);
  const localEditRevisionRef = useRef(0);
  const previousContextRef = useRef({ clientId: '', consultationId: '' });
  const [hydrationVersion, setHydrationVersion] = useState(0);

  const clientId = String(client?.id || '');
  const consultationId = String(consultation?.id || '');

  useEffect(() => {
    let cancelled = false;
    const previousContext = previousContextRef.current;
    const preserveUnsavedText = previousContext.clientId === clientId
      && !previousContext.consultationId
      && Boolean(consultationId)
      && localEditRevisionRef.current > 0;
    previousContextRef.current = { clientId, consultationId };
    contextVersionRef.current += 1;
    hydratedRef.current = false;
    revisionRef.current = 0;
    savedSignatureRef.current = '';
    clearTimeout(saveTimerRef.current);
    if (!preserveUnsavedText) {
      localEditRevisionRef.current = 0;
      latestRef.current = { text: '', reviewConfirmed: false };
      setText('');
      setReviewConfirmed(false);
    }
    setError(null);
    if (!clientId || !consultationId) {
      setDraftState('unavailable');
      return () => { cancelled = true; };
    }
    const editRevisionAtLoadStart = localEditRevisionRef.current;
    setDraftState('loading');
    loadResultsExplanationDraft({ clientId, consultationId })
      .then((draft) => {
        if (cancelled) return;
        revisionRef.current = draft.revision;
        savedSignatureRef.current = `${draft.text}\u0000${draft.reviewConfirmed}`;
        const keepLocalText = preserveUnsavedText
          || localEditRevisionRef.current !== editRevisionAtLoadStart;
        hydratedRef.current = true;
        const hydrationMode = getResultsExplanationHydrationMode({
          keepLocalText,
          latest: latestRef.current,
          draft,
        });
        if (hydrationMode === 'save-local') {
          setDraftState('saving');
          setHydrationVersion((version) => version + 1);
          return;
        }
        localEditRevisionRef.current = 0;
        latestRef.current = { text: draft.text, reviewConfirmed: draft.reviewConfirmed };
        setText(draft.text);
        setReviewConfirmed(draft.reviewConfirmed);
        setDraftState('saved');
      })
      .catch((caught) => {
        if (cancelled) return;
        setDraftState('error');
        setError(String(caught?.message || caught));
      });
    return () => { cancelled = true; };
  }, [clientId, consultationId]);

  function persistDraft(snapshotText, snapshotReviewConfirmed) {
    if (!clientId || !consultationId) {
      return Promise.reject(new Error('Enregistre d’abord le plan pour pouvoir sauvegarder cette explication.'));
    }
    const signature = `${snapshotText}\u0000${snapshotReviewConfirmed}`;
    if (signature === savedSignatureRef.current) return saveQueueRef.current;
    const contextVersion = contextVersionRef.current;
    setDraftState('saving');
    const operation = saveQueueRef.current.catch(() => {}).then(async () => {
      if (contextVersion !== contextVersionRef.current) return null;
      const saved = await saveResultsExplanationDraft({
        clientId,
        consultationId,
        text: snapshotText,
        reviewConfirmed: snapshotReviewConfirmed,
        expectedRevision: revisionRef.current,
      });
      if (contextVersion !== contextVersionRef.current) return saved;
      revisionRef.current = saved.revision;
      savedSignatureRef.current = `${saved.text}\u0000${saved.reviewConfirmed}`;
      const isLatest = latestRef.current.text === snapshotText
        && latestRef.current.reviewConfirmed === snapshotReviewConfirmed;
      if (isLatest) {
        setReviewConfirmed(saved.reviewConfirmed);
        setDraftState('saved');
        setError(null);
      }
      return saved;
    }).catch((caught) => {
      if (contextVersion === contextVersionRef.current) {
        setDraftState('error');
        setError(String(caught?.message || caught));
      }
      throw caught;
    });
    saveQueueRef.current = operation;
    return operation;
  }

  useEffect(() => {
    latestRef.current = { text, reviewConfirmed };
    if (!hydratedRef.current) return undefined;
    const signature = `${text}\u0000${reviewConfirmed}`;
    if (signature === savedSignatureRef.current) return undefined;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      void persistDraft(text, reviewConfirmed).catch(() => {});
    }, 800);
    return () => clearTimeout(saveTimerRef.current);
  // persistDraft is intentionally driven by the exact primitive snapshots below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, reviewConfirmed, clientId, consultationId, hydrationVersion]);

  const profileSummary = useMemo(() => buildExternalProfileSummary(
    client?.form || {},
    { prenom: client?.prenom, nom: client?.nom },
  ), [client?.form, client?.nom, client?.prenom]);
  const promptText = useMemo(
    () => buildExternalResultsExplanationPrompt(profileSummary),
    [profileSummary],
  );
  const hasText = text.trim().length > 0;
  const wordCount = hasText ? text.trim().split(/\s+/).length : 0;

  async function copyPrompt() {
    if (!privacyConfirmed) return;
    try {
      await navigator.clipboard.writeText(promptText);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1800);
    } catch {
      setCopyState('error');
    }
  }

  function downloadPrompt() {
    if (!privacyConfirmed) return;
    const blob = new Blob([promptText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = EXTERNAL_RESULTS_EXPLANATION_PROMPT_FILENAME;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function handleExport() {
    if (exporting || !reviewConfirmed) return;
    clearTimeout(saveTimerRef.current);
    setError(null);
    setExporting(true);
    try {
      await persistDraft(text, true);
      await exportResultsExplanationToWord(
        client,
        consultation || { date: new Date().toISOString() },
        text,
      );
    } catch (caught) {
      setError(String(caught?.message || caught));
    } finally {
      setExporting(false);
    }
  }

  return (
    <section className="jrn-results-explanation" aria-labelledby="results-explanation-title">
      <div className="jrn-surface jrn-surface--quiet" role="note"
        style={{ marginBottom: 'var(--jrn-4)' }}>
        <strong>Document Word indépendant — cette validation ne crée ni ne valide le plan.</strong>
        {!hasPlan && (
          <>
            <p style={{ marginBottom: 'var(--jrn-3)' }}>
              Tu peux préparer ce document maintenant. Pour avancer dans le parcours, prépare puis adopte le premier plan.
            </p>
            <a href="#journey-first-plan" className="jrn-btn jrn-btn--soft">
              Revenir à la préparation du premier plan ↑
            </a>
          </>
        )}
      </div>
      <header className="jrn-results-explanation__hero">
        <div>
          <p className="jrn-results-explanation__eyebrow">Document autonome · Word</p>
          <h3 id="results-explanation-title">Expliquer ses analyses à la cliente</h3>
          <p>
            Prépare les instructions, utilise-les dans la conversation de travail où les PDF sont déjà
            joints, puis colle ici la réponse. Tu peux tout corriger avant l’export Word.
          </p>
        </div>
        <span className="jrn-results-explanation__badge">Voix d’Anissa</span>
      </header>

      <div className="jrn-results-explanation__steps">
        <article className="jrn-results-explanation__card">
          <span className="jrn-results-explanation__number" aria-hidden="true">1</span>
          <p className="jrn-results-explanation__label">Préparer la conversation</p>
          <h4>Copier le prompt d’explication</h4>
          <p>
            Le prompt demande un résumé court dans la voix d’Anissa, séparé en ADN, salivaire
            et sanguin, avec les points forts, les points à surveiller et le suivi utile.
          </p>
          <details className="jrn-results-explanation__profile-preview">
            <summary>Voir le profil factuel inclus dans le prompt</summary>
            <pre>{profileSummary}</pre>
          </details>
          <label className="jrn-results-explanation__check">
            <input
              type="checkbox"
              checked={privacyConfirmed}
              onChange={(event) => {
                setPrivacyConfirmed(event.target.checked);
                setCopyState('idle');
              }}
            />
            <span>J’ai relu le profil joint au prompt et vérifié qu’il ne contient aucune identité directe.</span>
          </label>
          <div className="jrn-results-explanation__actions">
            <button type="button" className="jrn-btn jrn-btn--hero"
              onClick={copyPrompt} disabled={!privacyConfirmed}>
              {copyState === 'copied' ? 'Prompt copié' : 'Copier le prompt'}
            </button>
            <button type="button" className="jrn-btn jrn-btn--soft"
              onClick={downloadPrompt} disabled={!privacyConfirmed}>
              Télécharger le prompt
            </button>
          </div>
          {copyState === 'error' && (
            <p className="jrn-results-explanation__error" role="alert">
              Copie automatique indisponible. Télécharge le prompt à la place.
            </p>
          )}
        </article>

        <article className="jrn-results-explanation__card jrn-results-explanation__card--editor">
          <span className="jrn-results-explanation__number" aria-hidden="true">2</span>
          <p className="jrn-results-explanation__label">Revenir dans le SaaS</p>
          <h4>Coller et relire l’explication</h4>
          <p>
            Colle la réponse obtenue. Ce texte reste entièrement éditable et séparé du plan
            alimentaire comme de l’application cliente.
          </p>
          <label htmlFor="results-explanation-text" className="sr-only">
            Explication des résultats destinée à la cliente
          </label>
          <textarea
            id="results-explanation-text"
            value={text}
            onChange={(event) => {
              const nextText = event.target.value;
              localEditRevisionRef.current += 1;
              latestRef.current = { text: nextText, reviewConfirmed: false };
              setText(nextText);
              setReviewConfirmed(false);
              setError(null);
            }}
            rows={18}
            spellCheck="true"
            placeholder="Colle ici l’explication complète obtenue…"
            className="jrn-results-explanation__textarea"
          />
          <div className="jrn-results-explanation__meter" aria-live="polite">
            <span>{wordCount} mot{wordCount > 1 ? 's' : ''}</span>
            <span>
              {draftState === 'loading' && 'Chargement du brouillon… Tu peux déjà coller le texte.'}
              {draftState === 'saving' && 'Enregistrement…'}
              {draftState === 'saved' && '✓ Sauvegardé automatiquement'}
              {draftState === 'error' && '⚠ Non enregistré'}
              {draftState === 'unavailable'
                && 'Collage disponible · non sauvegardé tant que le plan n’est pas enregistré'}
            </span>
          </div>
          <label className="jrn-results-explanation__check">
            <input
              type="checkbox"
              checked={reviewConfirmed}
              onChange={(event) => {
                const nextReviewConfirmed = event.target.checked;
                localEditRevisionRef.current += 1;
                latestRef.current = { text, reviewConfirmed: nextReviewConfirmed };
                setReviewConfirmed(nextReviewConfirmed);
                setError(null);
              }}
              disabled={!hasText}
            />
            <span>
              J’ai relu, corrigé et validé cette explication pour la cliente. Les chiffres et
              les unités correspondent aux PDF joints.
            </span>
          </label>
          <div className="jrn-results-explanation__actions">
            <button type="button" className="jrn-btn jrn-btn--ghost"
              onClick={handleExport} disabled={exporting || !hasText || !reviewConfirmed}>
              {exporting ? 'Création du Word…' : 'Télécharger en Word'}
            </button>
          </div>
          <p className="jrn-results-explanation__pdf-note">
            Besoin d’un PDF ? Télécharge le Word, puis choisis « Enregistrer au format PDF » dans Word.
          </p>
          {error && <div className="jrn-error" role="alert">{error}</div>}
        </article>
      </div>

      <p className="jrn-results-explanation__footnote">
        Rien n’est envoyé automatiquement vers un service externe ni publié dans l’application cliente.
        {draftState === 'unavailable'
          ? ' Tu peux coller et relire le texte maintenant ; enregistre le plan pour activer sa sauvegarde.'
          : ' Le brouillon est sauvegardé dans le dossier professionnel privé d’Anissa et revient après actualisation.'}
      </p>
    </section>
  );
}
